(() => {
  if (customElements.get('trexo-product-builder')) return;
  class ProductBuilder extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.form = this.querySelector('form');
      this.button = this.form.querySelector('button[type=submit]');
      this.error = this.querySelector('[data-pb-error]');
      this.money = new Intl.NumberFormat(this.dataset.currency === 'TRY' ? 'tr-TR' : document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      this.visual = this.querySelector('[data-pb-visual]');
      this.groups = [...this.querySelectorAll('details[data-pb-group]')];
      this.visual.replaceChildren();
      this.groups.forEach((group, index) => {
        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'pb-visual-tile';
        tile.dataset.pbGroupIndex = index;
        group.id ||= `pb-group-${this.querySelector('[data-pb-choice]').name}-${index}`;
        tile.setAttribute('aria-controls', group.id);
        if (group.tagName === 'DETAILS') {
          tile.setAttribute('aria-expanded', String(group.open));
          group.addEventListener('toggle', () => tile.setAttribute('aria-expanded', String(group.open)), {
            signal: this.abort.signal,
          });
        }
        const heading = document.createElement('strong');
        heading.textContent = group.dataset.label;
        const content = document.createElement('span');
        content.className = 'pb-visual-content';
        tile.append(heading, content);
        tile.addEventListener(
          'click',
          () => {
            if (group.tagName === 'DETAILS') group.open = true;
            group.scrollIntoView({
              block: 'center',
              behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
            });
            group.querySelector('[data-pb-base-variant], [data-pb-toggle]')?.focus({ preventScroll: true });
          },
          { signal: this.abort.signal },
        );
        this.visual.append(tile);
      });
      this.addEventListener('change', (e) => this.update(e.target), { signal: this.abort.signal });
      this.addEventListener(
        'click',
        (e) => {
          const toggle = e.target.closest('[data-pb-toggle]');
          const base = e.target.closest('[data-pb-base-variant]');
          if (toggle) {
            const input = toggle.closest('.pb-card').querySelector('[data-pb-choice]');
            input.checked = !input.checked;
            this.update(input);
          }
          if (base) {
            const select = base.closest('.pb-card').querySelector('select');
            select.value = base.dataset.pbBaseVariant;
            this.update(select);
          }
        },
        { signal: this.abort.signal },
      );
      this.form.addEventListener('submit', (e) => this.submit(e), { signal: this.abort.signal });
      this.update();
    }
    selected() {
      return [...this.querySelectorAll('[data-pb-choice]:checked:not(:disabled)')]
        .filter((input) => !input.closest('[data-pb-group]').hidden)
        .map((input) => ({ input, option: input.closest('.pb-card').querySelector('select').selectedOptions[0] }))
        .filter((x) => x.option && !x.option.disabled);
    }
    update(target) {
      const baseVariant = this.querySelector('.pb-base select').selectedOptions[0];
      this.groups.forEach((group, index) => {
        const variants = JSON.parse(group.dataset.variants || '""')
          .split(/\r?\n/)
          .map((name) => name.trim().toLowerCase())
          .filter(Boolean);
        const visible = !variants.length || variants.includes(baseVariant?.dataset.variantTitle?.trim().toLowerCase());
        group.hidden = !visible;
        this.visual.children[index].hidden = !visible;
        if (!visible) {
          group.open = false;
          group.querySelectorAll('[data-pb-choice]').forEach((input) => {
            input.checked = false;
          });
        }
      });
      this.querySelectorAll('[data-pb-toggle]').forEach((button) => {
        const checked = button.closest('.pb-card').querySelector('[data-pb-choice]').checked;
        button.setAttribute('aria-pressed', String(checked));
        button.querySelector('span').textContent = checked ? '−' : '+';
      });
      this.querySelectorAll('[data-pb-base-variant]').forEach((button) => {
        button.setAttribute(
          'aria-pressed',
          String(button.dataset.pbBaseVariant === button.closest('.pb-card').querySelector('select').value),
        );
      });
      const entries = this.selected();
      this.groups.forEach((group, index) => {
        const tile = this.visual.children[index];
        const content = tile.querySelector('.pb-visual-content');
        const selections = entries.filter((entry) => group.contains(entry.input));
        tile.title = [group.dataset.label, ...selections.map((entry) => entry.option.dataset.title)].join(': ');
        tile.setAttribute('aria-label', tile.title);
        content.replaceChildren();
        tile.classList.toggle('is-selected', selections.length > 0);
        if (selections.length) {
          const option = selections[0].option;
          if (option.dataset.image) {
            const thumbnail = document.createElement('img');
            thumbnail.src = option.dataset.image;
            thumbnail.alt = '';
            content.append(thumbnail);
          }
          const label = document.createElement('span');
          label.textContent = selections.map((entry) => entry.option.dataset.title).join(' + ');
          content.append(label);
        } else {
          const option = group.querySelector('[data-pb-variant]')?.selectedOptions[0];
          if (option?.dataset.image) {
            const thumbnail = document.createElement('img');
            thumbnail.className = 'pb-visual-placeholder';
            thumbnail.src = option.dataset.image;
            thumbnail.alt = '';
            content.append(thumbnail);
          }
          const plus = document.createElement('span');
          plus.className = 'pb-visual-plus';
          plus.textContent = '+';
          plus.setAttribute('aria-hidden', 'true');
          content.append(plus);
        }
      });
      const summary = this.querySelector('[data-pb-summary]');
      summary.replaceChildren();
      let total = 0;
      entries.forEach(({ input, option }) => {
        total += Number(option.dataset.price) * Number(option.dataset.quantity);
        const li = document.createElement('li');
        li.textContent = `${input.closest('[data-pb-group]').dataset.label}: ${option.dataset.title} × ${option.dataset.quantity}`;
        summary.append(li);
      });
      this.querySelectorAll('[data-pb-variant]').forEach((select) => {
        const option = select.selectedOptions[0];
        const price = select.closest('.pb-card').querySelector('[data-pb-price]');
        const amount = document.createElement('span');
        amount.className = 'price-current';
        amount.textContent = option?.dataset.formatted || '';
        price.replaceChildren(amount);
        if (option?.dataset.compare) {
          const compare = document.createElement('s');
          compare.className = 'price-compare muted';
          compare.textContent = option.dataset.compare;
          price.append(compare);
        }
      });
      const preview = entries.find((entry) => entry.input.closest('.pb-base'))?.option;
      const price = this.querySelector('[data-pb-product-price]');
      if (price && preview) {
        price.querySelector('.price-current').textContent = preview.dataset.formatted;
        let compare = price.querySelector('.price-compare');
        if (preview.dataset.compare && !compare) {
          compare = document.createElement('s');
          compare.className = 'price-compare';
          price.append(compare);
        }
        if (compare) {
          compare.textContent = preview.dataset.compare || '';
          compare.hidden = !preview.dataset.compare;
        }
      }
      const stock = this.querySelector('[data-pb-stock-indicator]');
      if (stock && preview) {
        stock.dataset.state = preview.dataset.stockState;
        stock.querySelector('[data-stock-label]').textContent = preview.dataset.stockLabel;
      }
      this.querySelectorAll('[data-variant-package]').forEach((panel) => {
        panel.hidden = panel.dataset.variantPackage !== preview?.value;
      });
      const img = this.querySelector('.pb-stage > img');
      const productKey = [
        ...new Set(
          entries.filter(({ input }) => !input.closest('.pb-base')).map(({ input }) => input.dataset.productId),
        ),
      ]
        .sort()
        .join(',');
      const matches = [...this.querySelectorAll('[data-pb-combination]')].filter((rule) => {
        const ruleKey = [...new Set(rule.dataset.products.split(',').filter(Boolean))].sort().join(',');
        return (
          ruleKey === productKey && (!rule.dataset.variant || rule.dataset.variant === preview?.dataset.variantTitle)
        );
      });
      const combination = matches.find((rule) => rule.dataset.variant) || matches[0];
      const image = combination?.dataset.image || preview?.dataset.image;
      img.hidden = !image;
      if (!img.hidden) {
        img.removeAttribute('srcset');
        if (img.getAttribute('src') !== image) img.src = image;
        img.alt = combination?.dataset.alt || preview.dataset.title;
      }
      this.querySelector('[data-pb-total]').textContent = this.money.format(total / 100);
      this.valid = [...this.querySelectorAll('[data-required="true"]')]
        .filter((group) => !group.hidden)
        .every((group) => entries.some((x) => group.contains(x.input)));
      this.button.disabled = this.busy || !this.valid;
      this.error.textContent = '';
    }
    async submit(event) {
      event.preventDefault();
      if (this.busy || !this.valid) return;
      this.busy = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.error.textContent = '';
      const items = new Map();
      this.selected().forEach(({ option }) =>
        items.set(option.value, (items.get(option.value) || 0) + Number(option.dataset.quantity)),
      );
      try {
        const response = await fetch(`${this.dataset.cart}/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [...items].map(([id, quantity]) => ({ id, quantity })),
            sections: 'cart-drawer',
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();
        if (!response.ok)
          throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
        const opened = await window.TrexoCart?.showAddedItems(result, this.button);
        if (!opened) window.location.assign(this.dataset.cart);
      } catch (error) {
        this.error.textContent = error.message || this.dataset.error;
      } finally {
        this.busy = false;
        this.button.disabled = !this.valid;
        this.button.removeAttribute('aria-busy');
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
    }
  }
  customElements.define('trexo-product-builder', ProductBuilder);
})();
